import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import debounce from 'lodash.debounce';

import {
  getImageSources,
  getFaceboxOutlineStyleFromDOM,
} from '../../../../../util/photo';
import { IMAGE_SIZE_CODE } from '../../../../../constants/image-size-codes';
import Button from '../../../../../components/global/Button';
import { getErrorString } from '../../../../../util/normalize-errors';
import Alert from '../../../../../components/global/Alert';
import CustomControl from '../../../../../components/global/CustomControl';

class PhotoForFacesHighlight extends Component {
  static propTypes = {
    photoRecord: PropTypes.shape({
      id: PropTypes.string,
      thumbnail_urls: PropTypes.arrayOf(
        PropTypes.shape({
          url: PropTypes.string,
          width: PropTypes.number,
          height: PropTypes.number,
        }),
      ),
      watermarked_urls: PropTypes.arrayOf(
        PropTypes.shape({
          url: PropTypes.string,
          width: PropTypes.number,
          height: PropTypes.number,
        }),
      ),
    }),
    faces: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        faces: PropTypes.array,
      }),
    ),
    faceHighlights: PropTypes.arrayOf(
      PropTypes.shape({
        face: PropTypes.shape({
          id: PropTypes.string,
          faces: PropTypes.array,
        }),
      }),
    ),
    faceHighlightsUpdate: PropTypes.func,
    preferWatermarked: PropTypes.bool,
    maxSize: PropTypes.oneOf(Object.values(IMAGE_SIZE_CODE)),
    forceImageSize: PropTypes.bool,
    loadingHighlights: PropTypes.bool,
    onClose: PropTypes.func,
  };

  state = {
    updating: false,
    error: '',
    showFaceboxes: true,
  };

  componentDidMount() {
    window.addEventListener('resize', this.debouncedForceUpdate);
  }

  componentDidUpdate(prevProps) {
    const { photoRecord } = this.props;
    if (
      !!prevProps?.photoRecord?.id &&
      prevProps?.photoRecord?.id !== photoRecord?.id
    ) {
      // eslint-disable-next-line
      this.setState(() => ({ showFaceboxes: false }));
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.debouncedForceUpdate);
  }

  // eslint-disable-next-line
  wrapper = undefined;

  elem = undefined;

  debouncedForceUpdate = debounce(
    // Will force re-render
    () => this.forceUpdate(),
    100,
  );

  toggleFaceHighlight = async faces => {
    const { faceHighlightsUpdate, faceHighlights } = this.props;
    const { updating } = this.state;

    if (updating) {
      return;
    }

    const highlightedFacesIds = faces
      .map(face => {
        const highlightItem = faceHighlights.find(
          highlight => highlight.face.id === face.id,
        );

        return highlightItem;
      })
      .filter(highlightItem => !!highlightItem)
      .map(({ face }) => face.id);

    const newHighlightFaces = faces.filter(face => {
      return !highlightedFacesIds.find(
        highlightedFaceId => highlightedFaceId === face.id,
      );
    });

    try {
      this.setState(() => ({ updating: true }));
      const facesInput = [
        // Remove old highlights
        ...faceHighlights
          .filter(
            faceHighlight =>
              !highlightedFacesIds.includes(faceHighlight.face.id),
          )
          .map(({ face, rating, ...highlight }) => ({
            ...highlight,
            ...face,
            id: face.id,
            rating,
          })),
        // Add new highlights
        ...newHighlightFaces.map(face => ({
          id: face.id,
          rating: 1,
          ...face,
        })),
      ];

      await faceHighlightsUpdate(facesInput);
    } catch (error) {
      this.setState(() => ({ error: getErrorString(error) }));
    } finally {
      this.setState(() => ({ updating: false }));
    }
  };

  render() {
    const {
      photoRecord,
      preferWatermarked,
      maxSize,
      forceImageSize,
      faces,
      faceHighlights,
      loadingHighlights,
      onClose,
    } = this.props;
    const { error, updating, showFaceboxes } = this.state;

    const thumbs =
      preferWatermarked && photoRecord.watermarked_urls
        ? photoRecord.watermarked_urls
        : photoRecord.thumbnail_urls;

    const { src, srcSet } = getImageSources(thumbs, maxSize);
    const allFacesHighlighted = faceHighlights.length >= faces.length;

    return (
      <div className="face-tag-photo-viewer">
        {error && <Alert type="danger">{error}</Alert>}
        <div
          className="photo-viewer-image-wrapper"
          ref={elem => {
            this.wrapper = elem;
          }}
        >
          <div className="photo-highlight-actions">
            {faces.length > 0 && (
              <Button
                btnSize="xs"
                btnStyle={allFacesHighlighted ? 'danger' : 'primary'}
                className="mr-1"
                disabled={updating}
                onClick={() => {
                  if (allFacesHighlighted) {
                    // Remove all highlighted faces
                    const highlightedFaces = faceHighlights.map(
                      faceHighlight => faceHighlight.face,
                    );

                    this.toggleFaceHighlight(highlightedFaces);
                  } else {
                    // Highlight non-marked ones:
                    const newHighlightFaces = faces.filter(
                      face =>
                        !faceHighlights.find(
                          faceHighlight => faceHighlight.face.id === face.id,
                        ),
                    );

                    this.toggleFaceHighlight(newHighlightFaces);
                  }
                }}
              >
                {allFacesHighlighted
                  ? 'Remove all as Spotlight'
                  : 'Mark all as Spotlight'}
              </Button>
            )}
            {onClose && (
              <Button
                btnStyle="reset"
                onClick={onClose}
                disabled={updating}
                className="btn-content-dismiss btn-content-dismiss-white"
              >
                Close
              </Button>
            )}
          </div>
          {faces.length === 0 && !loadingHighlights && (
            <div className="face-tag-photo-no-faces">
              There aren&rsquo;t any faces in this photo. We might still be
              looking, please check back later.
            </div>
          )}
          <img
            src={src}
            srcSet={forceImageSize ? undefined : srcSet}
            alt=""
            className="photo-viewer-image"
            ref={elem => {
              this.image = elem;
            }} // Ensures the faces properly places upon
            // image change since the props arrive
            // before image is loaded.
            onLoad={() => {
              this.setState(() => ({ showFaceboxes: true }));
            }}
          />
          {showFaceboxes &&
            !loadingHighlights &&
            faces.map(face => {
              const highlightItem = faceHighlights.find(
                highlight => highlight.face.id === face.id,
              );

              const faceboxClasses = classNames({
                'photo-highlight-facebox': true,
                'photo-highlight-facebox-selected': !!highlightItem,
              });

              const [matchedPhoto] = face?.matched_photos?.records ?? [];

              return (
                <div
                  className={faceboxClasses}
                  style={getFaceboxOutlineStyleFromDOM({
                    facebox: face.facebox,
                    imageElement: this.image,
                    wrapperElement: this.wrapper,
                  })}
                  key={face.id}
                >
                  <Button
                    btnStyle="reset"
                    onClick={() => this.toggleFaceHighlight([face])}
                    disabled={updating}
                    className="photo-highlight-facebox-face"
                  />
                  <div className="photo-highlight-facebox-check">
                    {matchedPhoto && (
                      <Fragment>
                        <span className="d-block text-center text-dark text-bold">
                          {matchedPhoto.identity?.name ?? 'N/A'}
                        </span>
                        <hr className="my-0" />
                      </Fragment>
                    )}

                    <CustomControl
                      id={face.id}
                      type="checkbox"
                      size="xs"
                      checked={!!highlightItem}
                      label={
                        highlightItem
                          ? 'Marked as spotlight'
                          : 'Mark as spotlight'
                      }
                      onChange={() => this.toggleFaceHighlight([face])}
                      className="text-center m-0"
                    />
                  </div>
                </div>
              );
            })}
        </div>
      </div>
    );
  }
}

export default PhotoForFacesHighlight;
